<app-auth-layout
  [variant]="isNewPassRequired ? 'split' : 'scene'"
  [headline]="isNewPassRequired ? ('user.authAside.newPassword.headline' | transloco) : ''"
  [tagline]="isNewPassRequired ? ('user.authAside.newPassword.tagline' | transloco) : ''"
  [languages]="isNewPassRequired ? [] : languageOptions"
  [currentLanguage]="language"
  (languageChange)="selectLanguage($event)"
>
    <ng-container *ngIf="!isNewPassRequired">
      <h2 class="auth-title" id="welcome-title">{{ 'user.login.welcomeTitle' | transloco }}</h2>
      <p class="auth-subtitle">{{ 'user.login.welcomeSubtitle' | transloco }}</p>
    </ng-container>

    <ng-container *ngIf="!isNewPassRequired">
      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <div class="field-group">
          <label class="field-label" for="email">{{ 'user.login.email' | transloco }}</label>
          <input
            class="text-input"
            id="email"
            type="email"
            formControlName="email"
            autocomplete="username"
            [placeholder]="'user.login.placeholderEmail' | transloco"
          />
        </div>

        <div class="field-group">
          <label class="field-label" for="password">{{ 'user.login.password' | transloco }}</label>
          <div class="input-wrap">
            <input
              class="text-input"
              id="password"
              formControlName="password"
              autocomplete="current-password"
              [type]="showPassword ? 'text' : 'password'"
              [placeholder]="'user.login.placeholderPassword' | transloco"
            />
            <button
              class="eye"
              type="button"
              (click)="showPassword = !showPassword"
              [attr.aria-pressed]="showPassword"
              [attr.aria-label]="(showPassword ? 'user.login.hidePassword' : 'user.login.showPassword') | transloco"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/></svg>
            </button>
          </div>
        </div>

        <div class="form-options">
          <label class="remember">
            <input type="checkbox" [checked]="rememberEmail" (change)="rememberEmail = $any($event.target).checked" />
            {{ 'user.login.rememberEmail' | transloco }}
          </label>
          <button class="text-button" type="button" (click)="forgotPassword()">{{ 'user.login.forgotPassword' | transloco }}</button>
        </div>

        <button class="chamfer" type="submit" [disabled]="submitBtnLoading || checkingSession">
          {{ (submitBtnLoading ? 'user.login.signingIn' : 'user.login.signIn') | transloco }}
        </button>
        <button class="chamfer outline" type="button" (click)="signUp()">{{ 'user.login.signUp' | transloco }}</button>
      </form>

      <div class="divider">{{ 'user.login.orContinueWith' | transloco }}</div>
      <div class="oauth-buttons">
        <button class="oauth-btn" type="button" (click)="loginWithGoogle()">
          <svg class="oauth-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Google
        </button>
        <button class="oauth-btn oauth-apple" type="button" (click)="loginWithApple()">
          <i class="pi pi-apple oauth-icon" aria-hidden="true"></i>
          Apple
        </button>
      </div>
    </ng-container>

    <ng-container *ngIf="isNewPassRequired">
      <h2 class="auth-title">{{ 'user.account.changePassword.modal.title' | transloco }}</h2>
      <p class="auth-subtitle">{{ 'user.login.newPassSubtitle' | transloco }}</p>

      <form [formGroup]="changePasswordForm" (ngSubmit)="challengeNewPass()">
        <div class="field-group">
          <label class="field-label" for="email">{{ 'user.login.email' | transloco }}</label>
          <div class="input-wrap">
            <input class="text-input" id="email" type="email" formControlName="email" [readonly]="true" aria-readonly="true" />
            <span class="input-lock" aria-hidden="true">
              <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
            </span>
          </div>
        </div>

        <div class="field-group">
          <label class="field-label" for="newPassword">{{ 'user.account.changePassword.modal.inputNewPassword' | transloco }}</label>
          <div class="input-wrap">
            <input
              class="text-input"
              id="newPassword"
              formControlName="newPassword"
              autocomplete="new-password"
              [type]="showNewPassword ? 'text' : 'password'"
              [placeholder]="'user.account.changePassword.modal.inputNewPassword' | transloco"
            />
            <button
              class="eye"
              type="button"
              (click)="showNewPassword = !showNewPassword"
              [attr.aria-pressed]="showNewPassword"
              [attr.aria-label]="(showNewPassword ? 'user.login.hidePassword' : 'user.login.showPassword') | transloco"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/></svg>
            </button>
          </div>
          <app-password-strength-checklist [password]="changePasswordForm.get('newPassword')?.value"></app-password-strength-checklist>
        </div>

        <div class="auth-actions">
          <button class="chamfer" type="submit" [disabled]="submitBtnLoading">
            {{ (submitBtnLoading ? 'user.login.signingIn' : 'user.account.changePassword.modal.confirmButton') | transloco }}
          </button>
        </div>
      </form>
    </ng-container>
</app-auth-layout>

<p-toast key="toastMessages" />
